import React, { useState } from "react";
import { commitMutation, graphql, QueryRenderer } from "react-relay";

import { recreateTransform } from "@fellow/prosemirror-recreate-transform";
import cx from "classnames";
import { memoize } from "lodash";
import moment from "moment";
import { Change, ChangeSet, Span } from "prosemirror-changeset";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, Plugin } from "prosemirror-state";
import { Mapping, Transform } from "prosemirror-transform";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { Dimmer, List } from "semantic-ui-react";

import { showFeatureModal } from "~/billing";
import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getContext } from "~/lib/prose/plugins/context";
import { getBlockPos } from "~/lib/prose/utils";
import { isNative } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import NameList from "~/ui/avatar/NameList";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext } from "~/ui/modals";
import toasty from "~/ui/toast";

import { getAllMarks } from "../../lib/prose/marks";
import { CustomEmoji, Embed, HardBreak, HorizontalRule, KeyResult, Tag, TextNode } from "../../lib/prose/nodes";
import { Header as HeaderNode } from "../../lib/prose/nodes/header";
import Mention from "../../lib/prose/nodes/mention";
import { Agenda, Goal, Todo } from "../../lib/prose/plugins/assignable";
import Indent from "../../lib/prose/plugins/indent";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "./nodes/image-strip";
import { BulletPoint, NumberedListItem, Unstyled } from "./nodes/plaintext";
import BlockBoundary from "./plugins/blockBoundary";

import type {
	historyNoteQuery$data,
	historyNoteQuery as historyNoteQueryType,
} from "~/__generated__/historyNoteQuery.graphql";
import type { historyRecoverMutation as historyRecoverMutationType } from "~/__generated__/historyRecoverMutation.graphql";
import type { historyTransactionDetailQuery as historyTransactionDetailQueryType } from "~/__generated__/historyTransactionDetailQuery.graphql";

import styles from "./css/history.module.css";

interface ProseTransactionType {
	readonly id: string;
	readonly numSteps: number;
	readonly contributors: readonly {
		readonly id: string;
		readonly fullName: string;
		readonly image: string;
		readonly email: string;
	}[];
	readonly updatedAt: string | null;
	readonly createdAt: string | null;
}

interface HistoryModalProps {
	noteId: string;
	lastSeen?: Date;
	onClose: () => void;
}

interface HistoryTableProps extends historyNoteQuery$data {
	noteId: string;
	noteVersion: number;
	onClose: () => void;
	lastSeen?: Date;
	isMobile: boolean;
}

interface HistoryTableState {
	selected: string | null;
	restoring: boolean;
	selectedVersion: number | null;
}

const historyRecoverMutation = graphql`
	mutation historyRecoverMutation($input: RecoverToVersionInput!) {
		recoverToVersion(input: $input) {
			ok
		}
	}
`;

export class HistoryModal extends React.Component<HistoryModalProps> {
	static Query = graphql`
		query historyNoteQuery($noteId: ID!, $lastSeen: DateTime!, $queryLastSeen: Boolean!) {
			node(id: $noteId) {
				... on Note {
					id
					createdAt
					version
					proseTransactionBetween(startDateTime: $lastSeen) @include(if: $queryLastSeen) {
						id
						docs
						startVersion
						numSteps
						contributors {
							id
							fullName
							image
							email
						}
						updatedAt
						createdAt
					}
					proseTransactions {
						edges {
							node {
								id
								numSteps
								startVersion
								contributors {
									id
									fullName
									image
									email
								}
								updatedAt
								createdAt
							}
						}
					}
				}
			}
		}
	`;
	shouldComponentUpdate(prevProps: HistoryModalProps) {
		if (prevProps.noteId === this.props.noteId) return false;
		return true;
	}

	renderTable = ({ props, error }: { props: historyNoteQueryType["response"] | null; error: Error | null }) => {
		if (error || !props || !props.node) return <LoadingSpinner />;

		if (!props.node.version || !props.node.proseTransactions) return <NoHistoryModal onClose={this.props.onClose} />;

		const version = props.node.version;

		return (
			<Modal className={styles.modal} onClose={this.props.onClose}>
				<ViewportContext.Consumer>
					{ctx => (
						<HistoryTable
							{...props}
							lastSeen={this.props.lastSeen}
							onClose={this.props.onClose}
							noteId={this.props.noteId}
							noteVersion={version}
							isMobile={ctx.isMobile}
						/>
					)}
				</ViewportContext.Consumer>
			</Modal>
		);
	};

	render() {
		return (
			<QueryRenderer<historyNoteQueryType>
				query={HistoryModal.Query}
				environment={environment}
				variables={{
					noteId: this.props.noteId,
					lastSeen: this.props.lastSeen?.toISOString() || new Date().toISOString(),
					queryLastSeen: !!this.props.lastSeen,
				}}
				render={this.renderTable}
			/>
		);
	}
}

const previewPlugin = () => {
	return new Plugin({
		view: (_v: EditorView) => {
			return {
				update: (v: EditorView) => {
					const context = getContext(v.state);

					const mapPrimitive: Mapping | undefined = context.mapping;

					if (!mapPrimitive) return;
					const map = mapPrimitive.maps;
					for (let index = map.length - 1; index >= 0; index--) {
						const m = map[index];
						// @ts-ignore
						const p = m.ranges[0];
						const blockPos = getBlockPos(p, v.state.doc);
						if (!blockPos) {
							continue;
						}
						const dom = v.domAtPos(blockPos - 1).node;
						if (dom !== v.dom) {
							requestAnimationFrame(() => {
								(dom as HTMLElement).scrollIntoView({ block: "center", inline: "center" });
							});
							return;
						}
					}
				},
			};
		},
		props: {
			decorations: (state: EditorState) => {
				const context = getContext(state);

				const changeSet: ChangeSet<{}>["changes"] | undefined = context.changeSet;
				if (!changeSet) return;

				function invalidSlice(from: number, to: number): boolean {
					const slice = state.doc.slice(from, to);
					// Can't have an inline decoration span from outside to inside a node
					if (slice.openStart !== slice.openEnd) return true;
					return false;
				}

				const decorations: Decoration[] = [];
				changeSet.forEach((change: Change<{}>) => {
					change.inserted.forEach((span: Span) => {
						const { fromB, toB } = change;
						if (invalidSlice(fromB, toB)) return;
						decorations.push(Decoration.inline(fromB, toB, { class: "prose-diff-inserted prose-diff", ...span.data }));
					});

					change.deleted.forEach((span: Span) => {
						const { fromB, toB } = change;
						if (invalidSlice(fromB, toB)) return;
						decorations.push(Decoration.inline(fromB, toB, { class: "prose-diff-deleted prose-diff", ...span.data }));
					});
				});
				return DecorationSet.create(state.doc, decorations);
			},
		},
	});
};

const RenderTransactionPreview = ({
	startVersion,
	setSelectedVersion,
	hideVersionSelectionAndRestore,
	docs: rawDocs,
}: {
	startVersion: number;
	contributors: ProseTransactionType["contributors"];
	setSelectedVersion: (n: number) => void;
	hideVersionSelectionAndRestore: boolean;
	docs: NonNullable<NonNullable<historyTransactionDetailQueryType["response"]["node"]>["docs"]>;
}) => {
	const { user } = React.useContext(AppContext);

	const [slideValue, setSlideValue] = useState(rawDocs.length - 1);
	React.useEffect(() => {
		setSelectedVersion(startVersion + slideValue);
	}, [setSelectedVersion, startVersion, slideValue]);

	const config = React.useMemo(
		() =>
			new EditorConfig({
				plugins: [previewPlugin()],
				extensions: [
					new BlockBoundary(),
					...getAllMarks(),
					new TextNode(),
					new Indent(),
					new Unstyled(),
					new BulletPoint(),
					new NumberedListItem(),
					new HardBreak(),
					new HorizontalRule(),
					new HeaderNode({ inNoteHistory: true }),
					new Mention({ currentUserId: user?.id }),
					new Tag({ showToolbar: false, clickable: false }),
					new Todo({
						attendees: [],
						viewerId: user?.id,
					}),
					new Agenda({
						attendees: [],
						viewerId: user?.id,
					}),
					new ImageStrip({ disableWhenNotEditable: true }),
					new ImageStripItem(),
					new ImageStripItemFile(),
					new ImageStripItemCaption(),
					new Goal({ viewerId: user?.id }),
					new Embed(),
					new KeyResult(),
					new CustomEmoji(),
				],
				nodes: {
					doc: {
						content: "block+",
					},
				},
			}),
		[user?.id],
	);

	const docs: ProseNode[] = React.useMemo(
		() => rawDocs.map(doc => ProseNode.fromJSON(config.schema, doc)),
		[rawDocs, config],
	);

	const getDiffToStep = React.useMemo(
		() =>
			memoize(
				(
					stepNumber: number,
				): {
					controlledDoc: ProseNode;
					changeSet: ChangeSet<{}>["changes"] | null;
					mapping: Mapping | null;
				} => {
					/*
				Docs is a series of documents in order from oldest to newest
				Recreated on the backend from transactions.

				This function diffs the document as of stepNumber with the first document
				to determine the changes that have been applied to it.

				Insertions will be remembered for later
				Deletions will be re-inserted into a 3rd version of the document.

				The 3rd version of the document has all the insertions in it
				Along with all the deletions

				And a list of changes, along with the ranges, where code was inserted or deleted

				Later on in the previewPlugin the list of changes can be used to highlight inserted / deleted sections
				in green / red respectively
				*/
					if (stepNumber === 0) {
						return {
							controlledDoc: docs[0],
							changeSet: null,
							mapping: null,
						};
					}

					const initialDoc = docs[0];
					const doc = docs[stepNumber];

					// create a potential set of steps that would take you from the initial document to the current document
					let diff;
					try {
						diff = recreateTransform(initialDoc, doc, { complexSteps: false, wordDiffs: true, simplifyDiff: false });
					} catch (e) {
						// Failed to create a diff between the 2 docs, we'll just not show any
						// diff highlights for them.
						return {
							controlledDoc: doc,
							changeSet: null,
							mapping: null,
						};
					}

					const map = diff.mapping.maps;
					const changeSet = ChangeSet.create<{}>(initialDoc).addSteps(doc, map, { contributors: [] });

					// create a transform that will contain re-insertions of deleted sections
					let transform = new Transform(doc);

					// re-insert deleted sections
					changeSet.changes.forEach(change => {
						const { fromA, toA, fromB } = change;
						change.deleted.forEach(() => {
							const slice = initialDoc.slice(fromA, toA);

							// Don't insert "incomplete" stuff; if openStart and openEnd are not
							// the same that means that either the start or end of this slice is
							// nested inside a node that the other position is not.
							if (slice.openStart !== slice.openEnd) return;

							const insertLocation = transform.mapping.map(fromB);

							transform = transform.insert(insertLocation, slice.content);
						});
					});

					// create a mapping that goes from the start document to the end of all the above changes
					const mapping = diff.mapping;
					mapping.appendMapping(transform.mapping);

					// At this point transform.doc contains a document with both inserted sections
					// and deleted sections, but the changeSet's indexes are now outdated
					// We can however loop through them, and with a bit of math update the after (B) side
					const mapFromMiddleToEnd = transform.mapping.map.bind(transform.mapping);
					const changes = changeSet.changes
						.map(change => {
							const lengthOfInsertedBlock = change.inserted
								? mapFromMiddleToEnd(change.toB, 1) - mapFromMiddleToEnd(change.fromB, 1)
								: 0;

							// Changes & deletions need to be split as they now both exist in the document
							// An inserted word goes from `before` to `beforeafter`
							// and the deleted & inserted ranges need to match up with their sections
							return [
								...(change.deleted
									? [
											{
												deleted: change.deleted,
												inserted: [],
												fromA: change.fromA,
												toA: change.toA,
												fromB: mapFromMiddleToEnd(change.fromB, -1),
												toB: mapFromMiddleToEnd(change.toB, 1) - lengthOfInsertedBlock,
											},
										]
									: []),
								...(change.inserted
									? [
											{
												deleted: [],
												inserted: change.inserted,
												fromA: change.fromA,
												toA: change.toA,
												fromB: mapFromMiddleToEnd(change.fromB, 1),
												toB: mapFromMiddleToEnd(change.toB, 1),
											},
										]
									: []),
							];
						})
						.flat(1);

					const transformDoc = transform.doc;

					return { controlledDoc: transformDoc, changeSet: changes, mapping };
				},
			),
		[docs],
	);

	const { controlledDoc, changeSet, mapping } = getDiffToStep(slideValue);

	return (
		<div className="prose-history-preview">
			<ProseReact
				enableReactHost
				className="prose-history-preview__editor"
				editable={false}
				controlledDoc={controlledDoc}
				config={config}
				context={{
					participants: [],
					note: {},
					startDoc: docs[0],
					changeSet,
					mapping,
				}}
			/>
			{!hideVersionSelectionAndRestore && (
				<input
					type="range"
					min={0}
					max={docs.length - 1}
					value={slideValue}
					className="prose-history-preview__slider"
					onChange={e => {
						const newValue = parseInt(e.target.value, 10);
						setSlideValue(newValue);
					}}
				/>
			)}
		</div>
	);
};

export class HistoryTable extends React.Component<HistoryTableProps, HistoryTableState> {
	static historyTransactionDetailQuery = graphql`
		query historyTransactionDetailQuery($id: ID!) {
			node(id: $id) {
				... on ProseTransaction {
					id
					startVersion
					docs
					contributors {
						id
						fullName
						image
						email
					}
				}
			}
		}
	`;
	constructor(props: HistoryTableProps) {
		super(props);
		this.state = {
			selected:
				isNative || this.props.lastSeen ? "since_last_seen" : props.node?.proseTransactions?.edges[0]?.node?.id || null,
			restoring: false,
			selectedVersion: null,
		};
	}
	restoreSelectedVersion = () => {
		const { selected, selectedVersion } = this.state;
		if (!selected || selectedVersion === null) return;
		this.setState({ restoring: true });
		commitMutation<historyRecoverMutationType>(environment, {
			mutation: historyRecoverMutation,
			variables: { input: { noteId: this.props.noteId, version: selectedVersion } },
			onCompleted: (res, error) => {
				if (res.recoverToVersion && res.recoverToVersion.ok && (!error || !error.length)) {
					toasty.success("Note recovered successfully");
				} else {
					toasty.error("Unable to recover note from history. Please try again.");
				}
				this.setState({ restoring: false });
				this.props.onClose();
			},
		});
	};

	canRestore = () => !this.state.restoring && this.state.selectedVersion !== this.props.noteVersion;

	onListItemClick = (e: React.MouseEvent) => {
		this.setState({ selected: (e.currentTarget as HTMLElement).dataset.key || null });
	};

	onSinceLastSeenTransactionClicked = () => {
		this.setState({ selected: "since_last_seen" });
	};

	renderVersionListItem = (node: ProseTransactionType, isSinceLastSeen = false) => {
		if (!node || !node.numSteps) return;

		let isSelected = false;
		if (this.state.selected === node.id) {
			isSelected = true;
		} else if (isSinceLastSeen && this.state.selected === "since_last_seen") {
			isSelected = true;
		}

		return (
			<List.Item
				key={node.id}
				data-key={node.id}
				onClick={isSinceLastSeen ? this.onSinceLastSeenTransactionClicked : this.onListItemClick}
				className={cx(styles.noteHistoryTableItem, {
					[styles.noteHistoryTableItemActive]: isSelected,
				})}
			>
				<List.Content>
					<List.Header className={styles.noteHistoryTableHeader}>
						{isSinceLastSeen
							? `Since ${moment(node.createdAt || "").calendar()}`
							: moment(node.createdAt || "").calendar()}
					</List.Header>
					<p className={styles.noteHistoryTableSubHeader}>
						<span>{node.numSteps} edits</span> |{" "}
						<span>
							<NameList users={node.contributors} capitalize maxLength={5} />
						</span>
					</p>
				</List.Content>
			</List.Item>
		);
	};

	onSetSelectedVersion = (n: number) => {
		this.setState({ selectedVersion: n });
	};

	renderPreviewNote = ({
		props: queryResponse,
		error: queryError,
	}: {
		props: historyTransactionDetailQueryType["response"] | null;
		error: Error | null;
	}) => {
		if (
			!queryResponse ||
			queryError ||
			!queryResponse.node ||
			!queryResponse.node.docs ||
			!queryResponse.node.id ||
			!queryResponse.node.contributors ||
			typeof queryResponse.node.startVersion === "undefined"
		)
			return <LoadingSpinner />;
		const { startVersion } = queryResponse.node;
		const hideVersionSelectionAndRestore = this.state.selected === "since_last_seen";
		return (
			<RenderTransactionPreview
				startVersion={startVersion}
				contributors={queryResponse.node.contributors}
				setSelectedVersion={this.onSetSelectedVersion}
				hideVersionSelectionAndRestore={hideVersionSelectionAndRestore}
				docs={queryResponse.node.docs}
			/>
		);
	};

	renderPreview() {
		if (!this.state.selected) return;
		if (this.state.selected === "since_last_seen") {
			if (this.props.node?.proseTransactionBetween) {
				return this.renderPreviewNote({ props: { node: this.props.node?.proseTransactionBetween }, error: null });
			} else {
				if (!this.props.node || !this.props.node.proseTransactions || !this.props.node.proseTransactions.edges) return;
				const {
					node: {
						proseTransactions: { edges },
					},
				} = this.props;

				const firstNode = edges[0]?.node;

				if (!firstNode) {
					return "No version history for this note yet.";
				}

				this.setState({
					selected: firstNode.id,
				});
			}
		}
		return (
			<QueryRenderer<historyTransactionDetailQueryType>
				environment={environment}
				query={HistoryTable.historyTransactionDetailQuery}
				variables={{ id: this.state.selected }}
				render={this.renderPreviewNote}
			/>
		);
	}

	render() {
		if (!this.props.node || !this.props.node.proseTransactions || !this.props.node.proseTransactions.edges) return;
		const {
			node: {
				proseTransactions: { edges },
			},
		} = this.props;

		const sinceLastSeenSelected = this.state.selected === "since_last_seen";

		return (
			<>
				<Modal.Header onClose={this.props.onClose}>
					<Modal.Title className={styles.extraPadding}>Version history</Modal.Title>
				</Modal.Header>
				<Modal.Body className={styles.body}>
					<Dimmer.Dimmable dimmed={this.state.restoring}>
						<div className={styles.historyGrid}>
							{!this.props.isMobile && (
								<div className={cx(styles.historyGridColumn, styles.noteHistoryTableVersionList)}>
									<List className={cx(styles.cancelFirstListElementPaddingCollapse, styles.niceHR)}>
										{this.props.node.proseTransactionBetween
											? this.renderVersionListItem(this.props.node.proseTransactionBetween, true)
											: null}
										{edges.map(edge => (edge && edge.node ? this.renderVersionListItem(edge.node) : null))}
									</List>
								</div>
							)}
							<div className={cx(styles.historyGridColumn, styles.noteHistoryDiff)}>{this.renderPreview()}</div>
						</div>
						{this.state.restoring && <LoadingSpinner dimmer />}
					</Dimmer.Dimmable>
				</Modal.Body>
				<Modal.Footer>
					<Button
						size="normal"
						variant="primary"
						onClick={this.restoreSelectedVersion}
						disabled={
							this.state.restoring || !this.canRestore() || !this.state.selectedVersion || sinceLastSeenSelected
						}
					>
						{this.state.selectedVersion && !sinceLastSeenSelected
							? this.state.selectedVersion === this.props.noteVersion
								? "Current version"
								: `Restore this version: ${this.state.selectedVersion} edits`
							: "Restore"}
					</Button>
				</Modal.Footer>
			</>
		);
	}
}

interface NoHistoryModalProps {
	onClose: () => void;
}

class NoHistoryModal extends React.PureComponent<NoHistoryModalProps> {
	render() {
		const { onClose } = this.props;

		return (
			<Modal onClose={onClose} width="27.5em">
				<Modal.Header noBottomBorder onClose={onClose}>
					<Modal.Title>Version History</Modal.Title>
				</Modal.Header>
				<Modal.Body textOnly>No version history for this note yet.</Modal.Body>
				<Modal.Footer>
					<Button size="normal" variant="primary" onClick={onClose}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		);
	}
}

export function openNoteHistoryModal(noteId: string, hasAccessToVersioning: boolean, lastSeen?: Date) {
	if (hasAccessToVersioning) {
		__globalModalContext?.open(({ close }) => <HistoryModal noteId={noteId} onClose={close} lastSeen={lastSeen} />);
	} else {
		showFeatureModal("versioning");
	}
}
